import React, { useEffect, useState } from "react";
import { Trophy, Calendar, Lock, CircleNotch, CaretRight } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import { api } from "../lib/api";
import TeamFlag from "../components/TeamFlag";
import MatchBetsModal from "../components/MatchBetsModal";
import UserBetsModal from "../components/UserBetsModal";

const STAGE_ORDER = ["PLAYOFFS", "LAST_32", "LAST_16", "QUARTER_FINALS", "SEMI_FINALS", "THIRD_PLACE", "FINAL"];

function getStageIndex(stage) {
  const idx = STAGE_ORDER.indexOf(stage);
  return idx === -1 ? 999 : idx;
}

function getStageLabel(stage, t) {
  switch (stage) {
    case "PLAYOFFS": return t("tab_playoffs");
    case "LAST_32": return "1/16";
    case "LAST_16": return "1/8";
    case "QUARTER_FINALS": return "1/4";
    case "SEMI_FINALS": return "1/2";
    case "THIRD_PLACE": return t("stage_third_place");
    case "FINAL": return t("stage_final");
    default: return stage.replace("_", " ");
  }
}

function formatMatchTime(utcDateStr, locale) {
  try {
    const d = new Date(utcDateStr);
    return d.toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

export default function Standings() {
  const { t, locale } = useI18n();
  const { activeGroup } = useActiveGroup();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Navigation tabs: 'groups' or 'playoffs'
  const [activeTab, setActiveTab] = useState("groups");
  
  // Selection states
  const [selectedGroupIdx, setSelectedGroupIdx] = useState(0);
  const [selectedStage, setSelectedStage] = useState("");
  
  // Modals for viewing predictions
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Swipe Gestures for Mobile Navigation (Sofascore-style)
  const touchStartX = React.useRef(0);
  const touchStartY = React.useRef(0);
  const touchEndX = React.useRef(0);
  const touchEndY = React.useRef(0);

  const tournamentId = activeGroup?.tournament_id;

  useEffect(() => {
    if (!tournamentId) {
      setData(null);
      return;
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/tournaments/${tournamentId}/standings`);
        setData(res.data);
        
        // Auto-select the current knockout stage (first stage with unfinished matches, or latest stage)
        const knockoutStages = Object.keys(res.data.knockout || {});
        if (knockoutStages.length > 0) {
          const sorted = knockoutStages.sort((a, b) => getStageIndex(a) - getStageIndex(b));
          const currentStage = sorted.find((stage) => {
            const stageMatches = res.data.knockout[stage] || [];
            return stageMatches.some((m) => m.status !== "FINISHED");
          });
          setSelectedStage(currentStage || sorted[sorted.length - 1] || sorted[0]);
        }
      } catch (err) {
        console.error("Failed to load standings", err);
        setError(t("standings_working_hard"));
      } finally {
        setLoading(false);
      }
    })();
  }, [tournamentId, t]);

  if (!tournamentId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center animate-in fade-in duration-500">
        <div className="w-16 h-16 bg-[#41a42b]/10 text-[#41a42b] rounded-2xl flex items-center justify-center mb-6">
          <Trophy size={32} weight="fill" />
        </div>
        <h1 className="font-display text-2xl text-neutral-800 mb-2">
          {t("no_active_tournament")}
        </h1>
        <p className="text-neutral-500 max-w-xs text-sm">
          {t("select_group_instructions")}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <CircleNotch size={36} className="animate-spin text-[#41a42b] mb-4" />
        <span className="text-xs text-neutral-500 tracking-wider uppercase font-bold">
          {t("loading_dots")}
        </span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="w-16 h-16 bg-neutral-100 text-neutral-400 rounded-2xl flex items-center justify-center mb-6">
          <Trophy size={32} weight="fill" />
        </div>
        <h1 className="font-display text-xl text-neutral-800 mb-2">
          {t("tournament_standings")}
        </h1>
        <p className="text-neutral-500 max-w-sm text-sm leading-relaxed">
          {error}
        </p>
      </div>
    );
  }

  const { standings, knockout, teams } = data;
  const hasKnockout = Object.keys(knockout || {}).length > 0;
  
  // Sort stages in logical order
  const availableStages = Object.keys(knockout || {}).sort(
    (a, b) => getStageIndex(a) - getStageIndex(b)
  );

  // Active group standings table
  const activeStandingBlock = standings?.[selectedGroupIdx] || null;
  const groupPills = standings?.map((s, idx) => ({
    label: s.group ? s.group.replace("Group ", "") : `#${idx + 1}`,
    idx,
  })) || [];

  // Active playoff matches list
  const activePlayoffMatches = knockout?.[selectedStage] || [];

  // Helper to resolve flag properties
  const getTeamProps = (apiTeam) => {
    if (!apiTeam || !apiTeam.id) return null;
    const dbTeam = teams[apiTeam.id] || {};
    return {
      name: apiTeam.name || dbTeam.name || "TBD",
      crest_url: apiTeam.crest || dbTeam.crest_url || null,
      iso_code: dbTeam.iso_code || null,
      flag: dbTeam.flag || null,
    };
  };

  const handleMatchClick = (match) => {
    // Only allow clicking to view tips if match started or is finished
    const isLocked = new Date(match.utcDate) < new Date() || match.status === "FINISHED";
    if (isLocked && match.homeTeam?.id && match.awayTeam?.id) {
      setSelectedMatchId(match.id);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  };

  const handleTouchEnd = () => {
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = touchStartY.current - touchEndY.current;

    // Trigger horizontal swipe if horizontal movement is larger than vertical and exceeds threshold (60px)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 60) {
      const isLeftSwipe = diffX > 0;
      const isRightSwipe = diffX < 0;

      if (activeTab === "groups" || !hasKnockout) {
        if (isLeftSwipe && selectedGroupIdx < groupPills.length - 1) {
          setSelectedGroupIdx((prev) => prev + 1);
        } else if (isRightSwipe && selectedGroupIdx > 0) {
          setSelectedGroupIdx((prev) => prev - 1);
        }
      } else if (activeTab === "playoffs") {
        const currentIdx = availableStages.indexOf(selectedStage);
        if (isLeftSwipe && currentIdx < availableStages.length - 1) {
          setSelectedStage(availableStages[currentIdx + 1]);
        } else if (isRightSwipe && currentIdx > 0) {
          setSelectedStage(availableStages[currentIdx - 1]);
        }
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen pb-12 animate-in fade-in duration-300">
      {/* Tab Selector */}
      {hasKnockout && (
        <div className="flex bg-neutral-100 p-1 rounded-xl mx-4 mt-4 mb-2.5">
          <button
            type="button"
            onClick={() => setActiveTab("groups")}
            className={`flex-1 text-center py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${
              activeTab === "groups" ? "bg-white text-[#02371b] shadow-sm" : "text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {t("tab_groups")}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("playoffs")}
            className={`flex-1 text-center py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${
              activeTab === "playoffs" ? "bg-white text-[#02371b] shadow-sm" : "text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {t("tab_playoffs")}
          </button>
        </div>
      )}

      {/* Group Stage Tab */}
      {(activeTab === "groups" || !hasKnockout) && (
        <div className="flex flex-col">
          {/* Horizontal Swiper for Groups */}
          {groupPills.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto px-4 py-2 scrollbar-none select-none">
              {groupPills.map((p) => {
                const active = p.idx === selectedGroupIdx;
                return (
                  <button
                    key={p.idx}
                    type="button"
                    onClick={() => setSelectedGroupIdx(p.idx)}
                    className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-display text-sm font-bold border transition-all ${
                      active
                        ? "bg-[#02371b] text-white border-[#02371b]"
                        : "bg-white text-[#02371b] border-neutral-200 hover:bg-neutral-50"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Standings Table Card */}
          {activeStandingBlock && (
            <div
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="mx-4 mt-2 bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm select-none"
            >
              <div className="font-display text-sm font-bold text-[#02371b] mb-3">
                {activeStandingBlock.group || t("tournament_standings")}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-neutral-100 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                      <th className="py-2.5 w-7 text-center">#</th>
                      <th className="py-2.5 pl-2">{t("team")}</th>
                      <th className="py-2.5 w-9 text-center">{t("played_short")}</th>
                      <th className="py-2.5 w-10 text-center">{t("goals_difference_short")}</th>
                      <th className="py-2.5 w-9 text-center text-[#41a42b]">{t("points_short")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeStandingBlock.table?.map((row) => {
                      const teamProps = getTeamProps(row.team);
                      // Highlight qualified positions (e.g. top 2 in groups)
                      const isQualifying = row.position <= 2;
                      return (
                        <tr
                          key={row.team.id}
                          className="border-b border-neutral-50 last:border-0 text-sm align-middle"
                        >
                          <td className="py-3 text-center">
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-md text-[11px] font-bold ${
                                isQualifying
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "text-neutral-400"
                              }`}
                            >
                              {row.position}
                            </span>
                          </td>
                          <td className="py-3 pl-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {teamProps && <TeamFlag team={teamProps} size={20} className="w-5 h-5" />}
                              <span className="font-bold text-neutral-800 truncate">
                                {teamProps?.name || row.team.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 text-center text-neutral-500 font-medium font-mono-tab">
                            {row.playedGames}
                          </td>
                          <td className="py-3 text-center text-neutral-500 font-medium font-mono-tab">
                            {row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference}
                          </td>
                          <td className="py-3 text-center font-bold font-mono-tab text-[#41a42b]">
                            {row.points}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Playoffs Tab */}
      {activeTab === "playoffs" && hasKnockout && (
        <div className="flex flex-col">
          {/* Round Selector Tab List */}
          <div className="flex items-center gap-2 overflow-x-auto px-4 py-2 scrollbar-none select-none">
            {availableStages.map((stage) => {
              const active = stage === selectedStage;
              return (
                <button
                  key={stage}
                  type="button"
                  onClick={() => setSelectedStage(stage)}
                  className={`flex-shrink-0 px-4 py-2 h-9 rounded-xl flex items-center justify-center text-xs font-bold transition-all border ${
                    active
                      ? "bg-[#02371b] text-white border-[#02371b] shadow-sm"
                      : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"
                  }`}
                >
                  {getStageLabel(stage, t)}
                </button>
              );
            })}
          </div>

          {/* Playoffs List of Match Cards */}
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="px-4 mt-2 flex flex-col gap-2.5 min-h-[40vh] select-none"
          >
            {activePlayoffMatches.length === 0 ? (
              <div className="text-center py-10 text-sm text-neutral-500">
                {t("no_matches")}
              </div>
            ) : (
              activePlayoffMatches.map((m) => {
                const homeProps = getTeamProps(m.homeTeam);
                const awayProps = getTeamProps(m.awayTeam);
                
                const isFinished = m.status === "FINISHED";
                const isLive = m.status === "IN_PLAY" || m.status === "PAUSED";
                const isPlayed = isFinished || isLive;
                
                const matchStartDate = formatMatchTime(m.utcDate, locale);
                const isLocked = new Date(m.utcDate) < new Date() || isFinished;
                
                return (
                  <div
                    key={m.id}
                    onClick={() => handleMatchClick(m)}
                    className={`bg-white border border-neutral-200 rounded-2xl p-3 shadow-sm flex flex-col transition-all ${
                      isLocked && m.homeTeam?.id && m.awayTeam?.id
                        ? "cursor-pointer hover:border-neutral-300 active:bg-neutral-50/50"
                        : ""
                    }`}
                  >
                    {/* Card Header (Kickoff Time + Status) */}
                    <div className="flex items-center justify-between text-[10px] font-bold tracking-wider uppercase text-neutral-400 mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar size={12} />
                        <span>{matchStartDate}</span>
                      </div>
                      <div className="flex items-center gap-1.5 font-sans font-bold">
                        {isLive && (
                          <span className="flex items-center gap-1 text-red-600 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                            LIVE
                          </span>
                        )}
                        {isFinished && <span className="text-[#41a42b]">{t("actual")}</span>}
                        {!isPlayed && isLocked && (
                          <span className="text-neutral-400 flex items-center gap-0.5">
                            <Lock size={10} weight="bold" /> {t("locked")}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Body (Home and Away details) */}
                    <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                      {/* Teams details */}
                      <div className="flex flex-col gap-2.5 min-w-0">
                        {/* Home Row */}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <TeamFlag team={homeProps} size={22} className="w-5.5 h-5.5" />
                          <span className={`text-sm truncate ${homeProps ? "font-bold text-neutral-800" : "italic text-neutral-400"}`}>
                            {homeProps?.name || t("tbd")}
                          </span>
                        </div>
                        {/* Away Row */}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <TeamFlag team={awayProps} size={22} className="w-5.5 h-5.5" />
                          <span className={`text-sm truncate ${awayProps ? "font-bold text-neutral-800" : "italic text-neutral-400"}`}>
                            {awayProps?.name || t("tbd")}
                          </span>
                        </div>
                      </div>

                      {/* Score or Caret */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isPlayed ? (
                          <div className="flex flex-col items-center gap-2 font-mono text-base font-bold text-[#02371b]">
                            <div className="h-5.5 flex items-center">{m.score?.regularTime?.home ?? m.score?.fullTime?.home ?? 0}</div>
                            <div className="h-5.5 flex items-center">{m.score?.regularTime?.away ?? m.score?.fullTime?.away ?? 0}</div>
                          </div>
                        ) : (
                          <div className="text-neutral-300 font-mono text-sm">—</div>
                        )}
                        
                        {/* Render a navigation indicator if they can click to view tips */}
                        {isLocked && m.homeTeam?.id && m.awayTeam?.id && (
                          <CaretRight size={14} className="text-neutral-300 ml-1 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Modals for tips */}
      {selectedMatchId && (
        <MatchBetsModal
          matchId={selectedMatchId}
          groupId={activeGroup?.group_id}
          onClose={() => setSelectedMatchId(null)}
          onUserClick={(uid) => {
            setSelectedUserId(uid);
          }}
        />
      )}

      {selectedUserId && (
        <UserBetsModal
          userId={selectedUserId}
          tournamentId={tournamentId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}
